import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FormerChampions = () => {
  const { t } = useTranslation();

  // Championship data with active user IDs where available
  const championsData = [
    { name: 'Clutch Janni', titles: 6, userId: 14, isActive: true },
    { name: 'Truck', titles: 6, userId: null, isActive: false },
    { name: 'Sticky', titles: 5, userId: null, isActive: false },
    { name: 'Schniedlwixn aka Hertharobbi', titles: 4, userId: null, isActive: false },
    { name: 'Wicked', titles: 2, userId: null, isActive: false },
    { name: 'Gianni La Bamba', titles: 2, userId: null, isActive: false },
    { name: 'rib-rob710', titles: 3, userId: 10, isActive: true },
    { name: 'CrunchtimeMelo', titles: 1, userId: null, isActive: false },
    { name: 'Carmelo88', titles: 1, userId: null, isActive: false },
    { name: 'Max Carambo', titles: 1, userId: null, isActive: false },
    { name: 'NicJan', titles: 1, userId: null, isActive: false },
    { name: 'Sept Severus', titles: 1, userId: null, isActive: false },
    { name: 'Jano', titles: 1, userId: null, isActive: false },
    { name: 'Fliegi', titles: 1, userId: null, isActive: false },
    { name: 'Mr Bigglesberg', titles: 1, userId: null, isActive: false },
    { name: 'chocodon', titles: 1, userId: null, isActive: false },
    { name: 'BrotherLynch', titles: 1, userId: null, isActive: false },
    { name: 'Orkaydinho10', titles: 2, userId: 8, isActive: true }
  ];

  // Sort by titles (descending) then by name
  const sortedChampions = [...championsData].sort((a, b) => {
    if (b.titles !== a.titles) {
      return b.titles - a.titles;
    }
    return a.name.localeCompare(b.name);
  });

  // Get champions grouped by title count
  const championTiers = {
    legendary: sortedChampions.filter(c => c.titles >= 5),
    elite: sortedChampions.filter(c => c.titles >= 2 && c.titles < 5),
    champion: sortedChampions.filter(c => c.titles === 1)
  };

  const getTrophyIcon = (titles) => {
    if (titles >= 5) return '🏆';
    if (titles >= 2) return '🥇';
    return '🏅';
  };

  const getTierColor = (titles) => {
    if (titles >= 5) return 'from-yellow-400 to-yellow-600';
    if (titles >= 2) return 'from-slate-600 to-slate-800';
    return 'from-orange-600 to-orange-800';
  };

  const getTierBorderColor = (titles) => {
    if (titles >= 5) return 'border-yellow-500';
    if (titles >= 2) return 'border-slate-500';
    return 'border-orange-500';
  };

  const renderChampion = (champion) => {
    const ChampionContent = () => (
      <>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-3xl ${champion.titles >= 5 ? 'animate-pulse' : ''}`}>
            {getTrophyIcon(champion.titles)}
          </span>
          <span className={`text-2xl font-bold ${
            champion.isActive ? 'text-green-100' : 'text-white'
          }`}>
            {champion.titles}x
          </span>
        </div>
        <h3 className={`text-lg font-semibold mb-1 ${
          champion.isActive ? 'text-green-100' : 'text-white'
        }`}>
          {champion.name}
        </h3>
        {champion.isActive && (
          <div className="flex items-center gap-1 text-green-200 text-sm">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            {t('formerChampions.activeGM', 'Active GM')}
          </div>
        )}
      </>
    );

    if (champion.isActive && champion.userId) {
      return (
        <Link
          key={champion.name}
          to={`/owner/${champion.userId}`}
          className={`bg-gradient-to-br ${getTierColor(champion.titles)} rounded-xl p-6 text-white shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 border-2 ${getTierBorderColor(champion.titles)} hover:scale-105 cursor-pointer`}
        >
          <ChampionContent />
        </Link>
      );
    }

    return (
      <div
        key={champion.name}
        className={`bg-gradient-to-br ${getTierColor(champion.titles)} rounded-xl p-6 text-white shadow-xl border-2 ${getTierBorderColor(champion.titles)}`}
      >
        <ChampionContent />
      </div>
    );
  };

  const getTierTitle = (tier) => {
    switch(tier) {
      case 'legendary':
        return t('formerChampions.legendary', 'Legendary Champions (5+ Titles)');
      case 'elite':
        return t('formerChampions.elite', 'Elite Champions (2-4 Titles)');
      case 'champion':
        return t('formerChampions.oneTime', 'Champions (1 Title)');
      default:
        return '';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
          👑 {t('formerChampions.title', 'Former Champions Hall of Fame')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg max-w-3xl mx-auto">
          {t('formerChampions.description', 'Honoring the legends who have conquered our league throughout the years. These champions have earned their place in history through skill, dedication, and championship glory.')}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900 p-6 mb-8">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-200">
          📊 {t('formerChampions.statsTitle', 'Championship Statistics')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{sortedChampions.length}</div>
            <div className="text-sm text-blue-700 dark:text-blue-400">{t('formerChampions.totalChampions', 'Total Champions')}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">{sortedChampions.filter(c => c.isActive).length}</div>
            <div className="text-sm text-green-700 dark:text-green-400">{t('formerChampions.activeChampions', 'Still Active')}</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
            <div className="text-3xl font-bold text-yellow-600">{sortedChampions.reduce((sum, c) => sum + c.titles, 0)}</div>
            <div className="text-sm text-yellow-700 dark:text-yellow-400">{t('formerChampions.totalTitles', 'Total Titles')}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="text-3xl font-bold text-purple-600">{Math.max(...sortedChampions.map(c => c.titles))}</div>
            <div className="text-sm text-purple-700">{t('formerChampions.mostTitles', 'Most Titles')}</div>
          </div>
        </div>
      </div>

      {/* Championship Tiers */}
      {Object.entries(championTiers).map(([tier, champions]) => {
        if (champions.length === 0) return null;
        
        return (
          <div key={tier} className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-center text-gray-800 dark:text-gray-200">
              {getTierTitle(tier)}
            </h2>
            <div className={`grid gap-6 ${
              tier === 'legendary' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2' :
              tier === 'elite' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
              'grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
            }`}>
              {champions.map(renderChampion)}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mt-8">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
          📋 {t('formerChampions.legendTitle', 'Legend')}
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
            {t('formerChampions.activeIndicator', 'Active GM - Click to view profile')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🏆</span>
            {t('formerChampions.legendaryIcon', 'Legendary Champions (5+ titles)')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🥇</span>
            {t('formerChampions.eliteIcon', 'Elite Champions (2-4 titles)')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🏅</span>
            {t('formerChampions.championIcon', 'Champions (1 title)')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormerChampions;